import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowRight, ArrowLeft, Globe, TrendingUp, Users, Building2, Code, Shield } from 'lucide-react';

const WhyHyperLocalPage = () => {
  const reasons = [
    {
      icon: <Globe className="w-8 h-8" />,
      number: "01",
      title: "Google Is Forcing Hyper-Local Relevance",
      points: [
        "City-level intent matching",
        "Service-specific relevance",
        "Stronger entity signals",
        "Localized content depth"
      ],
      summary: "Generic \"Services\" pages no longer rank.",
      takeaway: "Winning now requires structured service × city coverage at scale."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      number: "02",
      title: "AI Lowered the Cost of Content — Not SEO Strategy",
      description: "AI made writing cheap. It did not make:",
      points: [
        "SEO architecture",
        "Internal linking",
        "Local intent modeling",
        "Compliance with Google guidelines"
      ],
      summary: "The bottleneck moved from content creation to execution quality.",
      takeaway: "Hyper Local exists at the new bottleneck."
    },
    {
      icon: <Users className="w-8 h-8" />,
      number: "03",
      title: "Local Competition Has Exploded",
      points: [
        "More local businesses online than ever",
        "Franchises expanding into multiple cities",
        "Agencies rolling out location pages en masse",
        "AI spam flooding search results"
      ],
      summary: "Standing still means losing ground.",
      takeaway: "Businesses must scale faster and safer than competitors."
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      number: "04",
      title: "Agencies Are Under Margin Pressure",
      description: "Agencies face:",
      points: [
        "Rising labor costs",
        "Slower delivery timelines",
        "Client churn due to slow results"
      ],
      needs: [
        "Repeatable systems",
        "Productized SEO",
        "Scalable fulfillment"
      ],
      takeaway: "Hyper Local becomes agency infrastructure, not just a tool."
    },
    {
      icon: <Code className="w-8 h-8" />,
      number: "05",
      title: "WordPress Is Still the Default for Local Businesses",
      description: "Despite new site builders:",
      points: [
        "WordPress powers ~45% of local SMB websites",
        "Especially dominant in home services",
        "Highly fragmented ecosystem with no standard solution"
      ],
      takeaway: "This creates a massive, underserved install base."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      number: "06",
      title: "Google Is Cracking Down on Low-Quality Local Pages",
      points: [
        "Thin content penalties",
        "AI spam detection",
        "Doorway page enforcement"
      ],
      summary: "The market is resetting.",
      takeaway: "Businesses must replace bad pages with compliant, structured alternatives."
    }
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="font-['Outfit'] text-xl font-bold text-white">Hyper Local</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="overline mb-4">The Market Opportunity</div>
          <h1 className="font-['Outfit'] text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Why Hyper Local, Why Now
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Six converging trends are creating the perfect window for local businesses and agencies to dominate their markets.
          </p>
        </div>
      </section>

      {/* Reasons */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-16">
          {reasons.map((reason, index) => (
            <div 
              key={index}
              className="card-base p-8 md:p-10"
              data-testid={`reason-${index}`}
            >
              <div className="flex items-start gap-6">
                <div className="hidden md:flex w-16 h-16 bg-indigo-500/10 rounded-2xl items-center justify-center text-indigo-400 flex-shrink-0">
                  {reason.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl font-['Outfit'] font-bold text-indigo-500/30">{reason.number}</span>
                    <h2 className="font-['Outfit'] text-xl md:text-2xl font-bold text-white">
                      {reason.title}
                    </h2>
                  </div>

                  {reason.description && (
                    <p className="text-slate-400 mb-4">{reason.description}</p>
                  )}

                  <ul className="space-y-2 mb-4">
                    {reason.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-300">
                        <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>

                  {reason.needs && (
                    <>
                      <p className="text-slate-400 mb-3 mt-6">They need:</p>
                      <ul className="space-y-2 mb-4">
                        {reason.needs.map((need, i) => (
                          <li key={i} className="flex items-start gap-3 text-slate-300">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                            {need}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {reason.summary && (
                    <p className="text-slate-500 italic mb-4">{reason.summary}</p>
                  )}

                  <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4 mt-4">
                    <p className="text-indigo-300 font-medium">
                      → {reason.takeaway}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-indigo-900/50 via-indigo-800/30 to-indigo-900/50 rounded-3xl p-12 md:p-16">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-6">
              The window is open. Will you move?
            </h2>
            <p className="text-slate-400 mb-8 text-lg max-w-xl mx-auto">
              Early movers are building their city page coverage now. In 12 months, the competition will be 10x harder.
            </p>
            <Link 
              to="/#lead-form" 
              className="btn-primary inline-flex items-center gap-2"
            >
              Get Early Access
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-['Outfit'] font-bold text-white">Hyper Local</span>
          </div>
          <div className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Hyper Local. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WhyHyperLocalPage;
